import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, Typography, Box, Grid, Button,
  Stack, Divider, CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getQuoteDetail, updateQuoteStatus } from '../../actions/quote';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const ModalDetalleCotizar = ({ open, onClose, quoteId, chatId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector(state => state.auth.user || state.user.user);
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  useEffect(() => {
    if (open && quoteId) {
      setLoading(true);
      dispatch(getQuoteDetail(quoteId))
        .then(data => {
          setQuote(data);
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setLoading(false);
        });
    }
  }, [open, quoteId, dispatch]);

  const handleUpdateStatus = async (status) => {
    setActionLoading(true);
    try {
      const updated = await dispatch(updateQuoteStatus(quoteId, status, chatId));
      setQuote(updated);
      
      if (status === 'aceptada') {
        onClose();
        const srvId = getStrId(updated.servicio);
        const provId = getStrId(updated.servidor);
        navigate(`/servicio/${provId}/${srvId}/reservar?quoteId=${quoteId}`);
      }
    } catch (err) {
      console.error(err);
      alert('Error al actualizar el estado de la cotización');
    } finally {
      setActionLoading(false);
    }
  };

  const getDaysRemaining = (createdAt, vigenciaStr) => {
    if (!createdAt || !vigenciaStr) return 'N/A';
    const createdDate = new Date(createdAt);
    let durationMs = 0;
    if (vigenciaStr.includes('hora')) {
      const hours = parseInt(vigenciaStr) || 24;
      durationMs = hours * 60 * 60 * 1000;
    } else if (vigenciaStr.includes('día')) {
      const days = parseInt(vigenciaStr) || 1;
      durationMs = days * 24 * 60 * 60 * 1000;
    } else {
      durationMs = 48 * 60 * 60 * 1000;
    }
    const expiryDate = new Date(createdDate.getTime() + durationMs);
    const diffMs = expiryDate - new Date();
    if (diffMs <= 0) return 'Expirada';
    
    if (diffMs < 24 * 60 * 60 * 1000) {
      const hours = Math.ceil(diffMs / (60 * 60 * 1000));
      return `${hours} horas restantes`;
    }
    const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
    return `${diffDays} días restantes`;
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!quote) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">No se encontró la cotización</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const isClient = getStrId(quote.user?._id || quote.user) === getStrId(authUser?._id);
  const isProvider = getStrId(quote.servidor?._id || quote.servidor) === getStrId(authUser?._id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ color: '#0E2465', fontWeight: 800, mb: 1 }}>
          Cotización Personalizada
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
            Concepto
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#0E2465', fontWeight: 800 }}>
            {quote.concepto}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={4}>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
              {(quote.modalidades?.length || 0) > 1 ? 'Modalidades' : 'Modalidad'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#0E2465', fontWeight: 700 }}>
              {(quote.modalidades?.length ? quote.modalidades : [quote.modalidad].filter(Boolean)).join(', ')}
            </Typography>
            {(quote.modalidades?.length || 0) > 1 && isClient && (
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                Elegirás una al reservar
              </Typography>
            )}
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
              Idioma
            </Typography>
            <Typography variant="body1" sx={{ color: '#0E2465', fontWeight: 700 }}>
              {quote.idioma || 'Español'}
            </Typography>
          </Grid>
          <Grid sx={{ textAlign: 'right' }} size={4}>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
              Costo Total
            </Typography>
            <Typography variant="h5" sx={{ color: '#0E2465', fontWeight: 900 }}>
              ${quote.costo?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
              Por {quote.unidad}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 0.5 }}>
            Vigencia
          </Typography>
          <Typography variant="body1" sx={{ color: '#0E2465', fontWeight: 700 }}>
            {getDaysRemaining(quote.createdAt || quote.date, quote.vigencia)}
          </Typography>
        </Box>

        {quote.notas && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, color: '#64748B' }}>
                <ChatBubbleOutlineIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Notas del proveedor
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#393939', lineHeight: 1.6 }}>
                {quote.notas}
              </Typography>
            </Box>
          </>
        )}

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: '#0E2465', fontWeight: 800, mb: 2 }}>
            ¿Por qué confiar en este proveedor?
          </Typography>
          <Grid container spacing={2}>
            <Grid size={4}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748B' }}>
                <ShieldOutlinedIcon sx={{ color: '#59C39A' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Identidad verificada</Typography>
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748B' }}>
                <StarBorderOutlinedIcon sx={{ color: '#0E2465' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>128 reseñas</Typography>
              </Stack>
            </Grid>
            <Grid size={4}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748B' }}>
                <GppGoodOutlinedIcon sx={{ color: '#7495F8' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Pago protegido</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ bgcolor: '#EFF6FF', p: 2, borderRadius: '12px', mb: 4 }}>
          <Typography variant="subtitle2" sx={{ color: '#0E2465', fontWeight: 800, mb: 1.5 }}>
            ¿Qué sigue?
          </Typography>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#7495F8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', mt: 0.2 }}>
                1
              </Box>
              <Typography variant="caption" sx={{ color: '#0E2465', fontWeight: 500 }}>
                Confirma el servicio con el proveedor
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#7495F8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', mt: 0.2 }}>
                2
              </Box>
              <Typography variant="caption" sx={{ color: '#0E2465', fontWeight: 500 }}>
                Selecciona la fecha y horario en la siguiente página
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#7495F8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold', mt: 0.2 }}>
                3
              </Box>
              <Typography variant="caption" sx={{ color: '#0E2465', fontWeight: 500 }}>
                Realiza el pago de forma segura en la plataforma
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={2} justifyContent="center">
          {quote.status === 'pendiente' ? (
            isClient ? (
              <>
                <Button
                  variant="contained"
                  onClick={() => handleUpdateStatus('declinada')}
                  disabled={actionLoading}
                  sx={{
                    bgcolor: '#FF827A', color: 'white', borderRadius: '8px',
                    px: 4, py: 1.2, textTransform: 'none', boxShadow: 'none',
                    '&:hover': { bgcolor: '#E06F67' }, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 1
                  }}
                >
                  Declinar <CloseIcon fontSize="small" />
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleUpdateStatus('aceptada')}
                  disabled={actionLoading}
                  sx={{
                    bgcolor: '#0E2465', color: 'white', borderRadius: '8px',
                    px: 4, py: 1.2, textTransform: 'none', boxShadow: 'none',
                    '&:hover': { bgcolor: '#0A1A4A' }, fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 1
                  }}
                >
                  Aceptar <ArrowForwardIcon fontSize="small" />
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  borderColor: '#0E2465', color: '#0E2465', borderRadius: '8px',
                  px: 6, py: 1.2, textTransform: 'none', fontWeight: 700,
                  '&:hover': { bgcolor: '#EFF6FF', borderColor: '#0E2465' }
                }}
              >
                Cerrar (Pendiente de Aceptación)
              </Button>
            )
          ) : (
            <Stack direction="column" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Box sx={{
                px: 3, py: 1, borderRadius: '24px',
                bgcolor: quote.status === 'aceptada' ? '#EBFDF5' : '#FEF2F2',
                color: quote.status === 'aceptada' ? '#59C39A' : '#FF827A',
                fontWeight: 700, fontSize: '0.9rem', border: '1px solid',
                borderColor: quote.status === 'aceptada' ? '#59C39A' : '#FF827A'
              }}>
                Cotización {quote.status === 'aceptada' ? 'Aceptada' : 'Declinada'}
              </Box>
              {quote.status === 'aceptada' && isClient ? (
                <Stack direction="column" alignItems="center" spacing={1.5}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      sx={{
                        borderColor: '#0E2465', color: '#0E2465', borderRadius: '8px',
                        px: 4, py: 1.2, textTransform: 'none', fontWeight: 700,
                        '&:hover': { bgcolor: '#EFF6FF', borderColor: '#0E2465' }
                      }}
                    >
                      Cerrar
                    </Button>
                    <Button
                      variant="contained"
                      disabled={quote.yaReservada}
                      onClick={() => {
                        onClose();
                        const srvId = getStrId(quote.servicio);
                        const provId = getStrId(quote.servidor);
                        navigate(`/servicio/${provId}/${srvId}/reservar?quoteId=${quoteId}`);
                      }}
                      sx={{
                        bgcolor: '#0E2465', color: 'white', borderRadius: '8px',
                        px: 4, py: 1.2, textTransform: 'none', boxShadow: 'none',
                        '&:hover': { bgcolor: '#0A1A4A' }, fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' }
                      }}
                    >
                      {quote.yaReservada ? 'Ya reservado' : <>Reservar ahora <ArrowForwardIcon fontSize="small" /></>}
                    </Button>
                  </Stack>
                  {quote.yaReservada && (
                    <Typography variant="caption" sx={{ color: '#64748B', textAlign: 'center' }}>
                      Ya realizaste una reserva con esta cotización. Puedes verla en tus reservas.
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  onClick={onClose}
                  sx={{
                    bgcolor: '#0E2465', color: 'white', borderRadius: '8px',
                    px: 6, py: 1.2, textTransform: 'none', boxShadow: 'none',
                    '&:hover': { bgcolor: '#0A1A4A' }, fontWeight: 700
                  }}
                >
                  Cerrar
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ModalDetalleCotizar;
